import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import User_Navbar from './User_Navbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import { FaArrowLeft, FaArrowRight, FaEye, FaSortAmountDownAlt, FaSortAmountUp, FaTrash } from 'react-icons/fa';
import generateCaptcha from '../../components/captcha';
import { useNavigate } from 'react-router-dom';
import MainButton from '../../components/ul/MainButton';
import ActionButton from '../../components/ul/ActionButton';
import { IoMdSearch } from 'react-icons/io';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDeleteSweep } from 'react-icons/md';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';
import getISTDate from '../../components/getISTDate';
import { FiUsers } from 'react-icons/fi';

const User = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [totalUser, setTotalUser] = useState(0);
    const [blockUser, setBloackUser] = useState(false);
    const [selectState, setSelectState] = useState('today');
    const limit = 200;
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAllUserData(currentPage, startDate, endDate, limit, search, sortOrder, blockUser);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.totalPage);
                setTotalUser(response?.totalRecord);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, sortOrder, startDate, endDate, blockUser, selectState]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1);
    };

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a user. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }
                const response = await Calling.deleteUser(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedUsers([]);
                    setSelectAll(false);
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleEdit = (id) => {
        window.open(`/manage/user/all/info/${id}`, '_blank');
    };

    const handleBlockUser = async (id, status) => {
        setLoader(true);
        try {
            const response = await Calling.blockUser(id, status);
            if (response?.status) {
                toast.success(response?.message);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error blocking user:', error);
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    const handleReportSort = () => {
        setSelectState('sort')
        if (sortOrder === 1) {
            setSortOrder(-1)
        } else {
            setSortOrder(1)
        }
    }

    const handleBlockFiletr = () => {
        setBloackUser(!blockUser)
        setSelectState('block')
    }

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');
        }
    }

    const handleSearchUser = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value)
        setSelectState('search')
    }

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);

            return () => clearTimeout(delayDebounce);
        }
    }, [search]);

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <FiUsers size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                        <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <User_Navbar selectState={'user'} />
                <hr className="border-main-border" />

                {/* Total Users Card */}
                <div className="m-4">
                    <div className="p-5 bg-main-th rounded-xl border border-main-blue/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-main-subfont">Total Users Created</p>
                                <p className="text-2xl font-bold text-main-font mt-1">
                                    {loader ? (
                                        <span className="flex items-center text-main-subfont text-base">
                                            <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Loading...
                                        </span>
                                    ) : (
                                        totalUser.toLocaleString()
                                    )}
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-main-blue/10">
                                <FiUsers size={28} className="text-main-blue" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Date Range Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton onClick={() => handleDateRangeFilter('today')} label={'Today'} selectState={selectState === 'today'} />
                            <MainButton onClick={() => handleDateRangeFilter('yesterday')} label={'Yesterday'} selectState={selectState === "yesterday"} />
                            <MainButton onClick={() => handleDateRangeFilter('thisWeek')} label={'This Week'} selectState={selectState === 'thisWeek'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisMonth')} label={'This Month'} selectState={selectState === 'thisMonth'} />
                            <MainButton onClick={() => handleDateRangeFilter('last6Months')} label={'Last 6 Months'} selectState={selectState === 'last6Months'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisYear')} label={'This Year'} selectState={selectState === 'thisYear'} />
                        </div>

                        {/* Date Inputs */}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="date"
                                value={startDate}
                                name="startDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${
                                    selectState === "range"
                                        ? 'border-main-blue text-main-blue bg-main-th'
                                        : 'border-main-border text-main-subfont bg-main-white'
                                } focus:border-main-blue focus:outline-none`}
                            />
                            <input
                                type="date"
                                value={endDate}
                                name="endDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${
                                    selectState === "range"
                                        ? 'border-main-blue text-main-blue bg-main-th'
                                        : 'border-main-border text-main-subfont bg-main-white'
                                } focus:border-main-blue focus:outline-none`}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={handleBlockFiletr}
                                selectState={selectState === 'block'}
                                label={blockUser ? 'Unblock' : 'Block'}
                            />
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className="flex items-center gap-2">
                                        {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                        Sort
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full lg:w-80">
                            <IoMdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont" size={20} />
                            <input
                                type="search"
                                className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue focus:outline-none transition-all"
                                value={search}
                                onChange={handleSearchUser}
                                placeholder="Search users..."
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedUsers.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleDeleteSelected(null)}
                                                label={<MdDeleteSweep size={20} />}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Referral</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Device</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={9} className="text-center py-12">
                                        <Loader />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((user, index) => (
                                    <tr key={user._id} className="hover:bg-main-thHover transition-colors">
                                        <td className="ps-4 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user._id)}
                                                onChange={() => handleSelectUser(user._id)}
                                                className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{index + 1 + (currentPage - 1) * limit}</td>
                                        <td className="px-4 py-4">
                                            <div 
                                                className="flex items-center gap-3 cursor-pointer" 
                                                onClick={() => handleEdit(user?._id)}
                                            >
                                                {user?.u_profile ? (
                                                    <img src={user.u_profile} alt={user.u_name} className="w-10 h-10 rounded-xl object-cover border border-main-border" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-xl bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                        {user?.u_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font">{user.u_name}</p>
                                                    <p className="text-xs text-main-subfont">{user.u_email || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="font-semibold text-main-font">{user.u_ref_count || 0}</span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg ${
                                                user.has_active_plan 
                                                    ? 'bg-main-th text-main-blue border border-main-blue/30' 
                                                    : 'bg-main-bg text-main-subfont border border-main-border'
                                            }`}>
                                                {user.has_active_plan ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont cursor-pointer" onClick={() => handleEdit(user?._id)}>
                                            {user.u_device || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4">
                                            {user.u_status ? (
                                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-main-th text-main-blue border border-main-blue/30">
                                                    <LuDot size={18} /> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-main-delete/10 text-main-delete border border-main-delete/30">
                                                    <LuDot size={18} /> Blocked
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatDate(user.createdAt)}</td>
                                        <td className="px-4 py-4">
                                            <ActionButtons
                                                v={user}
                                                handleDeleteSelected={handleDeleteSelected}
                                                handleEdit={handleEdit}
                                                handleBlockUser={handleBlockUser}
                                            />
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={9} className="py-12">
                                            <NotFound />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                                    currentPage === 1
                                        ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                        : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                                    currentPage === totalPages
                                        ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                        : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {currentPage}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPages}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default User;

const ActionButtons = ({ v, handleDeleteSelected, handleEdit, handleBlockUser }) => {
    const [showActions, setShowActions] = useState(false);

    const handleButtonClick = () => {
        setShowActions(!showActions);
    };

    return (
        <div className="relative inline-block">
            <ActionButton onClick={handleButtonClick} />

            {showActions && (
                <div className="absolute right-0 mt-2 w-44 bg-main-white border border-main-border rounded-xl shadow-lg z-10 overflow-hidden">
                    {/* Block Toggle */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-main-border">
                        <span className="text-sm text-main-font">Block User</span>
                        <label className="cursor-pointer">
                            <input
                                type="checkbox"
                                checked={v?.u_status}
                                onChange={() => handleBlockUser(v?._id, !v?.u_status)}
                                className="sr-only peer"
                                name="user_status"
                            />
                            <div className="relative w-10 h-5 bg-main-border rounded-full peer peer-checked:bg-main-delete after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-main-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                        </label>
                    </div>

                    {/* View Button */}
                    <button
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-main-blue hover:bg-main-th transition-colors"
                        onClick={() => handleEdit(v?._id)}
                    >
                        <FaEye size={16} /> View Details
                    </button>

                    {/* Delete Button */}
                    <button
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-main-delete hover:bg-main-delete/5 transition-colors"
                        onClick={() => handleDeleteSelected(v?._id)}
                    >
                        <FaTrash size={14} /> Delete User
                    </button>
                </div>
            )}
        </div>
    );
};